import { useEffect, useRef, useState } from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Mute POI/transit/business clutter so the customer focuses on streets and
// the selected point. The classic `styles` prop is still supported when no `mapId`
// is set (we removed mapId — the page never registered a real cloud style).
const MAP_STYLES = [
  { featureType: 'poi',          stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park',     stylers: [{ visibility: 'simplified' }] },
  { featureType: 'transit',      stylers: [{ visibility: 'off' }] },
  { featureType: 'road',         elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
];

// Crosshair / target — used for the "locate me" button. The concentric ring
// reads as "find my current position" in Google-Maps-vernacular.
const LocateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="8" />
    <line x1="12" y1="2"  x2="12" y2="5" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <line x1="2"  y1="12" x2="5"  y2="12" />
    <line x1="19" y1="12" x2="22" y2="12" />
    <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
  </svg>
);

// Arrow-out-of-box — the universal "share" affordance.
const ShareIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

// Renders the customer pin and click-to-pin. Retail serviceability remains a
// point decision from the server; the map never draws demo or proof geometry.
function MapLayers({ consult }) {
  const map = useMap();
  const markerRef = useRef(null);

  // Destructure stable setters so dep arrays can reference them directly
  // (ESLint react-hooks/exhaustive-deps requires direct variable references,
  // not property accesses like consult.setLocation).
  const { setLocation, location } = consult;

  // Click the map to pin a location. setLocation is a stable React setter, so
  // narrowing the deps avoids re-attaching the listener on every parent render.
  useEffect(() => {
    if (!map || !window.google) return;
    const listener = map.addListener('click', (e) => {
      setLocation({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    });
    return () => listener.remove();
  }, [map, setLocation]);

  // Keep the marker in sync with the selected location. The marker is a small
  // indigo circle with a white halo so it reads against satellite imagery too.
  useEffect(() => {
    if (!map || !window.google) return;
    if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }
    if (location) {
      markerRef.current = new window.google.maps.Marker({
        position: location, map, clickable: false,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: '#4f46e5',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3,
          scale: 8,
        },
      });
      map.panTo(location);
    }
  }, [map, location]);

  return null;
}

export default function ConsultMap({ consult }) {
  // Pulse the share button briefly on success so the customer sees that
  // the click actually did something (the agent's audio response is the
  // primary feedback but lags by ~1-3s).
  const [sharePulse, setSharePulse] = useState(false);
  // "اطلاع" toast for try-share-before-call — see comments on canShare below.
  const [shareHint, setShareHint] = useState(null);

  const locateMe = () => {
    if (!navigator.geolocation) return;
    // GPS only — sets the pin on the map. Does NOT send to the agent: that's
    // what the separate "اشتراک موقعیت" button is for. Silent on permission-
    // denied / no-GPS so any previous pin stays in place.
    navigator.geolocation.getCurrentPosition(
      (pos) => consult.setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
    );
  };

  // Share is enabled the moment a pin exists — works in BOTH text chat
  // (sends a customer message that announces the share + carries the
  // location to the API) and active voice call (DC / control frame in-
  // flight). The only blocker is having no pin yet.
  const canShare = !!consult.location;

  const shareLocation = () => {
    if (!consult.location) {
      setShareHint('ابتدا یک نقطه را روی نقشه انتخاب کنید یا «موقعیت فعلی من» را بزنید.');
      setTimeout(() => setShareHint(null), 4500);
      return;
    }
    const ok = consult.shareLocation();
    if (ok) {
      setSharePulse(true);
      setTimeout(() => setSharePulse(false), 900);
    } else {
      setShareHint('ارسال موقعیت موفق نبود. لطفاً دوباره تلاش کنید.');
      setTimeout(() => setShareHint(null), 4500);
    }
  };

  if (!API_KEY) {
    return <div className="consult-map-empty">کلید نقشه تنظیم نشده است.</div>;
  }

  return (
    <>
      <div className="consult-map-actions">
        <button
          type="button"
          className="consult-map-action consult-locate-btn"
          onClick={locateMe}
          aria-label="نمایش موقعیت فعلی من روی نقشه"
          title="نمایش موقعیت فعلی من"
        >
          <LocateIcon />
          <span>موقعیت فعلی من</span>
        </button>
        <button
          type="button"
          className={`consult-map-action consult-share-btn${sharePulse ? ' is-pulsing' : ''}`}
          onClick={shareLocation}
          aria-disabled={!canShare}
          aria-label="اشتراک موقعیتِ انتخاب‌شده با اپراتور"
          title={canShare ? 'اشتراکِ موقعیتِ روی نقشه با اپراتور (در چت یا تماس)' : 'ابتدا یک نقطه روی نقشه انتخاب کنید'}
        >
          <ShareIcon />
          <span>اشتراک موقعیت</span>
        </button>
      </div>
      {shareHint && (
        <div className="consult-map-hint" role="status">{shareHint}</div>
      )}
      <APIProvider apiKey={API_KEY} language="fa" region="IR">
        <Map
          defaultCenter={{ lat: 35.71, lng: 51.41 }}
          defaultZoom={12}
          gestureHandling="greedy"
          disableDefaultUI={true}
          clickableIcons={false}
          styles={MAP_STYLES}
          style={{ width: '100%', height: '100%' }}
        >
          <MapLayers consult={consult} />
        </Map>
      </APIProvider>
    </>
  );
}
